import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TrophyIcon, Loader2, UsersIcon, PrinterIcon, AwardIcon, ClockIcon } from 'lucide-react';
import axios from 'axios';
import learningLevelsData from '../../data/learningLevels.json';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const REFRESH_INTERVAL_MS = 8000;

const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PublicCompetitionParticipants = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { competitionId } = useParams();
  const navigate = useNavigate();

  const [competition, setCompetition] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [topThree, setTopThree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPagesApi, setTotalPagesApi] = useState(1);
  const [totalRankedCount, setTotalRankedCount] = useState(0);
  const [limit] = useState(100);

  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  const fetchCompetitionInfo = useCallback(async () => {
    try {
      const res = await publicApi.get(`/public/competitions/${competitionId}/info`);
      if (res.data?.success) {
        setCompetition(res.data.data);
      }
    } catch (e) {
      console.error('Competition info fetch failed:', e);
    }
  }, [competitionId]);

  const fetchTopThree = useCallback(async () => {
    try {
      const res = await publicApi.get(
        `/public/competitions/${competitionId}/leaderboard?page=1&limit=3`
      );
      if (res.data?.success) {
        setTopThree(res.data.data || []);
      }
    } catch (e) {
      console.error('Top three fetch failed:', e);
    }
  }, [competitionId]);

  const fetchLeaderboardPage = useCallback(
    async (page = 1, options = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      try {
        const res = await publicApi.get(
          `/public/competitions/${competitionId}/leaderboard?page=${page}&limit=${limit}`
        );
        if (res.data?.success) {
          const rows = res.data.data || [];
          setLeaderboard(rows);
          const pagination = res.data.pagination || {};
          setTotalPagesApi(pagination.pages || 1);
          setTotalRankedCount(pagination.total ?? rows.length);
          setCurrentPage(page);
        }
      } catch (e) {
        console.error('Leaderboard fetch failed:', e);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [competitionId, limit]
  );

  const refreshData = useCallback(
    async (page = 1, options = {}) => {
      await Promise.all([fetchLeaderboardPage(page, options), fetchTopThree()]);
    },
    [fetchLeaderboardPage, fetchTopThree]
  );

  useEffect(() => {
    fetchCompetitionInfo();
  }, [fetchCompetitionInfo]);

  useEffect(() => {
    refreshData(1);
  }, [refreshData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshData(currentPage, { silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [currentPage, refreshData]);

  const handlePageChange = (page) => {
    refreshData(page);
  };

  const getStudentName = (entry) => {
    if (!entry) return '';
    const full = entry.fullName || `${entry.firstName || ''} ${entry.lastName || ''}`.trim();
    return full || entry.username || '';
  };

  const getMedalColor = (rank) => {
    if (rank === 1) return 'text-amber-500';
    if (rank === 2) return 'text-slate-400';
    if (rank === 3) return 'text-emerald-700';
    return 'text-slate-600';
  };

  const getRowBackground = (rank) => {
    if (rank === 1) return 'bg-amber-50/80';
    if (rank === 2) return 'bg-slate-50/90';
    if (rank === 3) return 'bg-emerald-50/70';
    return 'bg-white';
  };

  const formatTime = (milliseconds) => {
    if (!milliseconds) return '00:00:00';
    const totalMs = parseInt(milliseconds, 10);
    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const centiseconds = Math.floor((totalMs % 1000) / 10);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(centiseconds).padStart(2, '0')}`;
  };

  const pageTitle = competition?.name || t('competitions.participantsList');
  const totalRounds = competition?.totalRounds;

  const handlePrint = () => {
    window.print();
  };

  if (loading && leaderboard.length === 0) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-sky-100/90 via-violet-50/40 to-amber-50/50"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="mx-auto max-w-6xl px-3 py-10 sm:px-4">
          <div className="rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-white to-sky-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-sky-600" aria-hidden strokeWidth={2.25} />
            <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  const emptyList = leaderboard.length === 0;
  const totalForPager = totalRankedCount;
  const totalPages = totalPagesApi;

  return (
    <>
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          .shadow-lg, .shadow-md, .shadow-xl, .shadow-2xl, .shadow-\\[0_8px_0_0_rgba\\(15\\,23\\,42\\,0\\.06\\)\\] {
            box-shadow: none !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
      <div
        className="min-h-screen bg-gradient-to-b from-sky-100/90 via-violet-50/40 to-amber-50/50"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="print:hidden border-b-[3px] border-slate-900/10 bg-white/90 shadow-sm" dir="ltr">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-3 py-3 sm:px-4">
            <span className="text-lg font-black tracking-tight text-sky-900 sm:text-xl">eSoroban</span>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                type="button"
                dir={isRTL ? 'rtl' : 'ltr'}
                onClick={() => navigate(-1)}
                className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-2xl border-[3px] border-slate-900/10 bg-white px-4 text-sm font-bold text-slate-800 shadow-[0_4px_0_0_rgba(15,23,42,0.08)] transition hover:bg-sky-50 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <ArrowLeftIcon className={`h-4 w-4 shrink-0 ${isRTL ? 'rotate-180' : ''}`} aria-hidden />
                {t('common.back')}
              </button>
              <button
                type="button"
                dir={isRTL ? 'rtl' : 'ltr'}
                onClick={handlePrint}
                className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-2xl border-[3px] border-sky-200 bg-sky-50 px-4 text-sm font-bold text-sky-900 shadow-sm transition hover:bg-sky-100 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <PrinterIcon className="h-4 w-4 shrink-0" aria-hidden />
                {t('common.print')}
              </button>
            </div>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10">
          <DailyExercisesHero
            title={pageTitle}
            description={t('competitions.leaderboardPageDescription')}
            icon={TrophyIcon}
            iconClassName="text-amber-600"
          />

          {topThree.length >= 3 && (
            <div className="grid grid-cols-1 gap-4 py-4 md:grid-cols-3 md:gap-5 md:py-6">
              <div className="order-2 md:order-1">
                <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-white px-5 py-8 text-center shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:shadow-md sm:py-9">
                  <div className="mb-3 flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-slate-200 bg-slate-100">
                      <AwardIcon className="h-9 w-9 text-slate-400" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="mb-1 text-3xl font-black text-slate-400">2</div>
                  <h3 className="mb-1 line-clamp-2 text-lg font-black text-slate-900">{getStudentName(topThree[1])}</h3>
                  <p className="mb-2 text-xs font-semibold text-slate-500">
                    {translateLevelName(topThree[1].levelName) || '—'}
                  </p>
                  <p className="text-2xl font-black tabular-nums text-violet-700">{topThree[1].totalScore}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">{t('competitions.score')}</p>
                </div>
              </div>

              <div className="order-1 md:order-2">
                <div className="rounded-[1.35rem] border-[3px] border-amber-300/90 bg-gradient-to-br from-amber-400 to-amber-600 px-6 py-9 text-center shadow-[0_8px_0_0_rgba(180,83,9,0.2)] md:-mt-1 md:scale-[1.02] sm:py-10">
                  <div className="mb-3 flex justify-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-[3px] border-white/90 bg-white shadow-md">
                      <TrophyIcon className="h-11 w-11 text-amber-600" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="mb-1 text-4xl font-black text-white">1</div>
                  <h3 className="mb-1 line-clamp-2 text-xl font-black text-white">{getStudentName(topThree[0])}</h3>
                  <p className="mb-2 text-xs font-semibold text-amber-100">
                    {translateLevelName(topThree[0].levelName) || '—'}
                  </p>
                  <p className="text-3xl font-black tabular-nums text-white">{topThree[0].totalScore}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-amber-100">{t('competitions.score')}</p>
                </div>
              </div>

              <div className="order-3">
                <div className="rounded-[1.35rem] border-[3px] border-emerald-200/90 bg-white px-5 py-8 text-center shadow-[0_8px_0_0_rgba(15,23,42,0.06)] transition hover:shadow-md sm:py-9">
                  <div className="mb-3 flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-emerald-200 bg-emerald-50">
                      <AwardIcon className="h-9 w-9 text-emerald-700" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="mb-1 text-3xl font-black text-emerald-700">3</div>
                  <h3 className="mb-1 line-clamp-2 text-lg font-black text-slate-900">{getStudentName(topThree[2])}</h3>
                  <p className="mb-2 text-xs font-semibold text-slate-500">
                    {translateLevelName(topThree[2].levelName) || '—'}
                  </p>
                  <p className="text-2xl font-black tabular-nums text-violet-700">{topThree[2].totalScore}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">{t('competitions.score')}</p>
                </div>
              </div>
            </div>
          )}

          <section className="relative overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
            {loading && leaderboard.length > 0 && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center rounded-[1.35rem] bg-white/55 backdrop-blur-[1px]"
                aria-busy="true"
              >
                <Loader2 className="h-10 w-10 animate-spin text-sky-600" strokeWidth={2.25} />
              </div>
            )}

            <div className="border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-violet-50/50 px-4 py-3 sm:px-5">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">
                {t('competitions.competitionRanking')}
              </h2>
            </div>

            {emptyList ? (
              <div className="px-6 py-14 text-center">
                <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl border-[3px] border-slate-200 bg-slate-50">
                  <UsersIcon className="h-8 w-8 text-slate-300" aria-hidden />
                </div>
                <p className="font-bold text-slate-600">{t('competitions.noStudents')}</p>
                <p className="mt-1 text-sm text-slate-500">{t('competitions.noStudentsDesc')}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="border-b-2 border-slate-200/90 bg-slate-50/80">
                        <th
                          className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          {t('competitions.rank')}
                        </th>
                        <th
                          className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          {t('common.name')}
                        </th>
                        <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                          {t('competitions.rounds')}
                        </th>
                        <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                          {t('competitions.totalScore')}
                        </th>
                        <th className="px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                          <div className="mx-auto max-w-[9rem] overflow-hidden whitespace-nowrap text-ellipsis [direction:rtl] text-left">
                            <span className="inline-block [direction:ltr]">{t('competitions.totalTime')}</span>
                          </div>
                        </th>
                        <th
                          className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          {t('competitions.level')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((participant, index) => (
                        <tr
                          key={participant.studentNumber || participant.username || index}
                          className={`border-b border-slate-100 transition-colors hover:bg-sky-50/50 ${getRowBackground(participant.rankPosition)}`}
                        >
                          <td className={`whitespace-nowrap px-3 py-3.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div
                              className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : 'justify-start'}`}
                            >
                              {participant.rankPosition <= 3 ? (
                                <>
                                  <AwardIcon className={`h-6 w-6 shrink-0 ${getMedalColor(participant.rankPosition)}`} />
                                  <span
                                    className="text-lg font-black tabular-nums"
                                    style={{
                                      color:
                                        participant.rankPosition === 1
                                          ? '#d97706'
                                          : participant.rankPosition === 2
                                            ? '#64748b'
                                            : '#15803d',
                                    }}
                                  >
                                    {participant.rankPosition}
                                  </span>
                                </>
                              ) : (
                                <span className="text-lg font-bold text-slate-600">{participant.rankPosition}</span>
                              )}
                            </div>
                          </td>
                          <td className={`min-w-0 max-w-[12rem] px-3 py-3.5 sm:max-w-none ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div className="break-words font-semibold text-slate-900">{getStudentName(participant)}</div>
                            <div className="truncate text-xs text-slate-500">{participant.studentNumber}</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-3.5 text-center tabular-nums text-slate-800">
                            {participant.completedRounds} / {totalRounds ?? '—'}
                          </td>
                          <td className="px-3 py-3.5 text-center">
                            <span className="text-lg font-black tabular-nums text-violet-700">{participant.totalScore}</span>
                          </td>
                          <td className={`whitespace-nowrap px-3 py-3.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div className={`inline-flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <ClockIcon className="h-4 w-4 shrink-0 text-sky-500" aria-hidden />
                              <span className="font-semibold tabular-nums text-slate-800">
                                {formatTime(participant.totalTimeMs)}
                              </span>
                            </div>
                          </td>
                          <td className={`px-3 py-3.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <span className="text-sm font-semibold text-slate-800">
                              {translateLevelName(participant.levelName) || '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="print:hidden border-t border-slate-200/90 bg-slate-50/80 px-4 py-4 sm:px-5">
                    <div
                      className={`flex flex-col items-center justify-between gap-4 sm:flex-row ${isRTL ? 'sm:flex-row-reverse' : ''}`}
                    >
                      <div className="text-sm text-slate-700">
                        {t('competitions.showing')}{' '}
                        <span className="font-bold text-sky-700">{(currentPage - 1) * limit + 1}</span> {t('competitions.to')}{' '}
                        <span className="font-bold text-sky-700">{Math.min(currentPage * limit, totalForPager)}</span>{' '}
                        {t('competitions.of')} <span className="font-bold text-sky-700">{totalForPager}</span>{' '}
                        {t('competitions.students')}
                      </div>

                      <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1 || loading}
                          className="min-h-[2.5rem] rounded-2xl border-[3px] border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('common.previous')}
                        </button>

                        <div className={`flex flex-wrap items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {currentPage > 3 && (
                            <>
                              <button
                                type="button"
                                onClick={() => handlePageChange(1)}
                                className="min-h-[2.5rem] rounded-xl border-[3px] border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 hover:bg-sky-50"
                              >
                                1
                              </button>
                              {currentPage > 4 && <span className="px-2 text-slate-400">...</span>}
                            </>
                          )}

                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            if (pageNum < 1 || pageNum > totalPages) return null;
                            if (currentPage > 3 && pageNum === 1) return null;
                            if (currentPage < totalPages - 2 && pageNum === totalPages) return null;

                            return (
                              <button
                                key={pageNum}
                                type="button"
                                onClick={() => handlePageChange(pageNum)}
                                className={`min-h-[2.5rem] rounded-xl border-[3px] px-3 text-sm font-bold transition ${
                                  currentPage === pageNum
                                    ? 'border-sky-500 bg-sky-500 text-white shadow-sm'
                                    : 'border-slate-200 bg-white text-slate-800 hover:bg-sky-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          {currentPage < totalPages - 2 && (
                            <>
                              {currentPage < totalPages - 3 && <span className="px-2 text-slate-400">...</span>}
                              <button
                                type="button"
                                onClick={() => handlePageChange(totalPages)}
                                className="min-h-[2.5rem] rounded-xl border-[3px] border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 hover:bg-sky-50"
                              >
                                {totalPages}
                              </button>
                            </>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages || loading}
                          className="min-h-[2.5rem] rounded-2xl border-[3px] border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default PublicCompetitionParticipants;
